'use client'

/***
 *
 * @file layouts/Column/index.tsx
 * @description Column layout will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Center
 * @since 13 Feb 2024
 *
 */

import React, { ReactNode } from "react";
// import "./Column.scss";
import { StyledColumn } from "@/shared/StyledComponents";
import { T_KeyValue } from "@/types/app.type";

/** defines type of props passed to Column functional component */
type T_Props = {
  /** children components to be rendered within the column layout */
  children: ReactNode,

  /** Unique identifier for a column */
  id?: string,

  numberOfColumns?: number,

  /** CSS class to be passed inside the Column div component */
  className?: string,

  /** CSS styles to be passed in the style attribute of the Column div component */
  customStyles?: React.CSSProperties | T_KeyValue
}

/**
 * Name: Column
 * 
 * Description: It takes in children as props and renders them within the div component as Column layout
 * 
 * @param param0 object containing the children to be rendered and other props
 * @example Parameter breakup
 * ```
 *  param0: {
 *    children,             //children components to be rendered
 *    className: "",        //className string is passed via contentful
 *    numberOfColumns: ""   //accepts number of columns to render for proper styling
 *    customStyles: {},     //style object passed via contentful
 *    id: ""             
 *  }
 * ```
 * @example T_Props
 * ```
 *  type T_Props = {
 *      children: ReactNode,        
 *      id?: string,                // optional
 *      numberOfColumns?: number,   // optional
 *      className?: string,         // optional
 *      customStyles?: any          // optional
 * }
 * ```
 * @returns an html that renders child components within an html div element as part of a Column layout
 */
const Column = ({ children, ...props }: T_Props) => {
  return (
    <StyledColumn id={props?.id || "clm"} className={`
      bp-col wrapper 
      bp-col-lg-${Math.floor(12 / (props.numberOfColumns || 1))} ${props?.className || ""}
      bp-col-md-${Math.floor(12 / ((props?.numberOfColumns && props?.numberOfColumns > 2 ? 2 : props.numberOfColumns) || 1))}
      bp-col-sm-${Math.floor(12 / 1)}
      bp-col-xs-${Math.floor(12 / 1)}
      `} data-testid="Column" style={{ ...props?.customStyles }}
    >
      {children}
    </StyledColumn>
  );
};

export default Column;
